/* eslint-disable @typescript-eslint/no-explicit-any */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Toast } from 'sharedUi/Toast';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { formatDate, formatTimestamp } from 'sharedUi/dateUtils';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useCanUpdate, useHasPermission } from '../../hooks/usePermissions';
import { useUserAuditNames } from '../../hooks/useUserLookup';
import { JOB_NAMES } from '../../constants/jobNames';
import { PERMISSIONS } from '../../constants/permissions';
import { ragsService } from '../../services/rags.service';
import { projectsService } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { ActionButton, EmptyState, PageSkeleton, StatusChip } from '../../components/common/panel';
import { Icon } from '../../components/common/icon';
import RagPlayground from './rag-playground';
import { versionTone } from './rag-status';
import {
  RagDependenciesPanel,
  RagObservabilityPanel,
  RagQueryHistoryPanel,
} from '../../components/rags/rag-insight-panels';

/** Base64 URL-safe id encoding — matches useSecureNavigation's view routes. */
const encodeId = (id: string): string => {
  try {
    return btoa(id).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch {
    return id;
  }
};

const th = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted';
const td = 'px-3 py-2.5 align-middle';

const RagView = () => {
  const { id, isReady, navigateToList, navigateToEdit } = useSecureNavigation({ entity: 'rag', basePath: '/rags' });
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  const canEdit = useCanUpdate(JOB_NAMES.RAG);
  const canActivate = useHasPermission(PERMISSIONS.RAG_VERSION_ACTIVATE);
  const canQuery = useHasPermission(PERMISSIONS.RAG_QUERY);
  const canReadExec = useHasPermission(PERMISSIONS.PROMPT_EXECUTION_READ);
  const [confirm, setConfirm] = useState<{ kind: 'activate' | 'rollback' | 'deactivate'; version?: number } | null>(null);
  const [validation, setValidation] = useState<Record<number, { ok: boolean; problems: string[] }>>({});
  const [playgroundOpen, setPlaygroundOpen] = useState(false);
  // The section currently in view — highlighted in the "RAG sections" nav.
  const [activeSection, setActiveSection] = useState('');

  const { data, isLoading } = useQuery({ queryKey: ['rags', id], queryFn: () => ragsService.getById(id!), enabled: isReady && !!id });
  const { data: versions = [] } = useQuery({ queryKey: ['rag-versions', id], queryFn: () => ragsService.listVersions(id!), enabled: isReady && !!id });
  // One name-resolved call for the active version's full config (KB -> pipeline
  // -> chunking -> embedding, retrieval, generation, eval suite) — so the view
  // shows names, not the ObjectIds a client-side lookup would leave on cross-scope refs.
  const { data: overview } = useQuery({
    queryKey: ['rag-overview', id], queryFn: () => ragsService.getOverview(id!),
    enabled: isReady && !!id, staleTime: 30 * 1000,
  });
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'], queryFn: projectsService.getAccessible, staleTime: 5 * 60 * 1000,
  });
  const { createdByName, updatedByName } = useUserAuditNames(data?.createdBy, data?.updatedBy ?? undefined);
  useBreadcrumbTitle(data?.name);

  const refresh = () => { queryClient.invalidateQueries({ queryKey: ['rags', id] }); queryClient.invalidateQueries({ queryKey: ['rag-versions', id] }); queryClient.invalidateQueries({ queryKey: ['rags'] }); };
  const notifyError = (e: any, fallback: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, fallback), life: 12000 });

  const activate = useMutation({
    mutationFn: (version: number) => ragsService.activateVersion(id!, version),
    onSuccess: (v) => {
      setConfirm(null); refresh();
      // Activated, but the regression check against the live version could not run —
      // said out loud, not left silent (backend releaseGateNote).
      const note = (v as any).releaseGateNote as string | undefined;
      toast.current?.show(note
        ? { severity: 'warn', summary: `v${v.version} is now live`, detail: `Not compared with the previous live version: ${note}`, life: 12000 }
        : { severity: 'success', summary: 'Activated', detail: `v${v.version} is now live`, life: 4000 });
    },
    onError: (e) => { setConfirm(null); refresh(); notifyError(e, 'Activation was refused'); },
  });
  const rollback = useMutation({
    mutationFn: () => ragsService.rollback(id!),
    onSuccess: (v) => { setConfirm(null); refresh(); toast.current?.show({ severity: 'success', summary: 'Rolled back', detail: `v${v.version} is live again`, life: 4000 }); },
    onError: (e) => { setConfirm(null); notifyError(e, 'Rollback failed'); },
  });
  const deactivate = useMutation({
    mutationFn: () => ragsService.deactivate(id!),
    onSuccess: () => { setConfirm(null); refresh(); toast.current?.show({ severity: 'success', summary: 'Deactivated', detail: 'Nothing serves queries now. Roll back to bring the version back.', life: 5000 }); },
    onError: (e) => { setConfirm(null); notifyError(e, 'Deactivation failed'); },
  });
  const validate = async (version: number) => {
    try { const r = await ragsService.validateVersion(id!, version); setValidation((s) => ({ ...s, [version]: { ok: r.ok, problems: r.problems } })); }
    catch (e) { notifyError(e, 'Validation failed'); }
  };

  // Scrollspy: highlight the section in view once the RAG (and its anchors) load.
  useEffect(() => {
    if (!data) return;
    const els = Array.from(document.querySelectorAll('[id^="sec-"]')) as HTMLElement[];
    if (!els.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-80px 0px -60% 0px' },
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [data?._id]);

  if (!isReady || !id || isLoading) return <PageSkeleton panels={2} />;
  if (!data) {
    return (
      <div className="p-4">
        <EmptyState
          icon="pi pi-search"
          title="This RAG could not be found"
          hint="It may have been deleted, or the link points at another project."
          action={<span className="ui-press inline-flex"><ActionButton kind="secondary" icon="arrow-left" label="Back to RAGs" onClick={() => navigateToList()} /></span>}
        />
      </div>
    );
  }

  const active = versions.find((v) => v.status === 'ACTIVE');
  const hasRetired = versions.some((v) => v.status === 'RETIRED');
  const pct = (n?: number | null) => (n == null ? '—' : `${(n * 100).toFixed(0)}%`);
  const projectLabel = (appId?: string | null) => {
    if (!appId) return 'Global / Shared';
    const p = accessibleProjects.find((x) => x._id === appId);
    return p ? (p.appCode ? `${p.name} (${p.appCode})` : p.name) : appId;
  };
  const suiteName = data.defaultEvalSuiteRef ? (overview?.evaluation?.suiteName || data.defaultEvalSuiteRef) : 'Any rag suite';
  const k = overview?.knowledge;
  const gen = overview?.generation;
  const ret = overview?.retrieval;
  /** A fact value that links to a record when we have the id + a view route. */
  const linked = (base: string, refId?: string | null, label?: string | null) =>
    (refId && label
      ? <Link to={`${base}/${encodeId(refId)}`} className="text-blue-600 hover:underline">{label}</Link>
      : (label || '—'));

  // A timestamp with its actor underneath; either half may be missing.
  const stamp = (at: unknown, by: string) => {
    const when = at ? formatTimestamp(at as string) : '';
    const who = by && by !== '-' ? by : '';
    if (!when && !who) return '–';
    return (<>{when && <span className="tabular-nums">{when}</span>}{who && <span className="block text-xs text-ink-muted">{who}</span>}</>);
  };

  const scrollToId = (sid: string) => {
    const el = document.getElementById(sid);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveSection(sid);
    el.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2');
    setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2'), 1200);
  };

  const navItems = [
    { id: 'sec-knowledge', label: active ? 'Knowledge' : 'Serving' },
    active && { id: 'sec-retrieval', label: 'Retrieval' },
    active && { id: 'sec-generation', label: 'Generation' },
    data.description && { id: 'sec-description', label: 'Description' },
    { id: 'sec-used-by', label: 'Used by' },
    { id: 'sec-queries', label: 'Query history' },
    canReadExec && { id: 'sec-observability', label: 'Observability' },
    { id: 'sec-versions', label: `Versions (${versions.length})` },
  ].filter(Boolean) as { id: string; label: string }[];

  return (
    <div className="mx-auto max-w-[1280px] px-3 pb-10 pt-2 sm:px-5" data-testid="SCR-Rag-View">
      <Toast ref={toast} position="top-right" />

      <button type="button" onClick={() => navigateToList()} className="pd-back ui-press">
        <Icon name="arrow-left" size={14} /> RAGs
      </button>

      {/* Header: what this is, whether it is serving, and the actions that change that. */}
      <header className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h1 className="text-title font-semibold text-ink" style={{ textWrap: 'balance' }}>{data.name}</h1>
            <span className="font-mono text-xs text-ink-muted">{data.ragCode}</span>
            <StatusChip tone={data.status === 'ACTIVE' ? 'success' : 'neutral'}>{data.status}</StatusChip>
            <StatusChip tone={data.activeVersion != null ? 'info' : 'neutral'} dot={false}>
              {data.activeVersion != null ? `serving v${data.activeVersion}` : 'not serving'}
            </StatusChip>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {canActivate && data.activeVersion != null && <ActionButton kind="danger" icon="ban" label="Deactivate" onClick={() => setConfirm({ kind: 'deactivate' })} testId="Rag.Deactivate" />}
          {canActivate && hasRetired && <ActionButton kind="secondary" icon="history" label="Roll back" onClick={() => setConfirm({ kind: 'rollback' })} />}
          {canEdit && <ActionButton kind="secondary" icon="sliders" label="Designer" onClick={() => navigateToEdit(id)} />}
          {canQuery && <ActionButton kind="primary" icon="play" label="Playground" onClick={() => setPlaygroundOpen(true)} testId="Rag.Playground" />}
        </div>
      </header>

      <div className="mt-6 grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_21rem]">
        {/* Main column */}
        <div className="flex min-w-0 flex-col gap-4">
          {active ? (
            <>
              {/* Knowledge — the indexed corpus behind every answer. */}
              <section id="sec-knowledge" className="ui-panel p-5 scroll-mt-20">
                <h2 className="text-sm font-semibold text-ink">Knowledge</h2>
                <p className="mt-0.5 text-xs text-ink-muted">The corpus and index the active version reads, from its pipeline artifact.</p>
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-3">
                  <div><span className="pd-label">Knowledge base</span><p className="pd-text">{linked('/knowledge-bases', k?.kbRef, k?.kbName)}</p></div>
                  <div><span className="pd-label">Pipeline</span><p className="pd-text">{linked('/pipelines', k?.pipelineRef, k?.pipelineCode || k?.pipelineName)}</p></div>
                  <div><span className="pd-label">Artifact</span><p className="pd-text font-mono text-xs">{k?.artifactCode || active?.artifactCode || '—'}</p></div>
                  <div><span className="pd-label">Embedding model</span><p className="pd-text">{k?.embeddingModel || '—'}{k?.embeddingDimensions ? ` · ${k.embeddingDimensions}d` : ''}</p></div>
                  <div><span className="pd-label">Chunking</span><p className="pd-text">{k?.chunking?.strategy
                    ? `${k.chunking.strategy}${k.chunking.chunkSize != null ? ` · ${k.chunking.chunkSize}` : ''}${k.chunking.chunkOverlap != null ? ` / ${k.chunking.chunkOverlap} overlap` : ''}`
                    : '—'}</p></div>
                  <div><span className="pd-label">Indexed</span><p className="pd-text">{k?.chunkCount != null ? `${k.chunkCount.toLocaleString()} chunks` : '—'}{k?.sourceCount != null ? ` · ${k.sourceCount} sources` : ''}</p></div>
                </div>
              </section>

              {/* Retrieval — how the context is selected. */}
              <section id="sec-retrieval" className="ui-panel p-5 scroll-mt-20">
                <h2 className="text-sm font-semibold text-ink">Retrieval</h2>
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
                  <div><span className="pd-label">Top-K</span><p className="pd-text">{ret?.topK ?? '—'}</p></div>
                  <div><span className="pd-label">Min score</span><p className="pd-text">{ret?.minimumScore ?? '—'}</p></div>
                  <div><span className="pd-label">Max chunks</span><p className="pd-text">{ret?.maxContextChunks ?? '—'}</p></div>
                  <div><span className="pd-label">Context budget</span><p className="pd-text">{ret?.maxContextTokens != null ? `${ret.maxContextTokens.toLocaleString()} tok` : '—'}</p></div>
                  <div><span className="pd-label">Reranking</span><p className="pd-text">{ret?.reranking?.enabled ? 'On' : 'Off'}</p></div>
                  <div><span className="pd-label">Filters</span><p className="pd-text">{ret?.filters?.enabled ? 'On' : 'Off'}</p></div>
                  <div><span className="pd-label">Candidate pool</span><p className="pd-text">{ret?.candidateMultiplier != null ? `${ret.candidateMultiplier}×` : '—'}</p></div>
                  <div><span className="pd-label">Per-source cap</span><p className="pd-text">{ret?.maxPerSource ?? 'none'}</p></div>
                </div>
              </section>

              {/* Generation — how the answer is composed. */}
              <section id="sec-generation" className="ui-panel p-5 scroll-mt-20">
                <h2 className="text-sm font-semibold text-ink">Generation</h2>
                <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4 lg:grid-cols-4">
                  <div><span className="pd-label">Mode</span><p className="pd-text">{gen?.enabled ? 'Generate answer' : 'Retrieval only'}</p></div>
                  <div><span className="pd-label">Prompt</span><p className="pd-text">{gen?.enabled ? linked('/prompt-table', gen?.promptRef, gen?.promptName) : '—'}</p></div>
                  <div><span className="pd-label">Model</span><p className="pd-text">{gen?.enabled ? (gen?.modelName || 'Platform default') : '—'}</p></div>
                  <div><span className="pd-label">Max tokens</span><p className="pd-text">{gen?.maxTokens != null ? gen.maxTokens.toLocaleString() : '—'}</p></div>
                </div>
              </section>
            </>
          ) : (
            <section id="sec-knowledge" className="ui-panel p-5 scroll-mt-20">
              <h2 className="text-sm font-semibold text-ink">Serving</h2>
              <p className="mt-2 text-sm text-ink-muted">No active version yet, so nothing is serving queries. Open the Designer to compose and activate one.</p>
            </section>
          )}

          {data.description && (
            <section id="sec-description" className="ui-panel p-5 scroll-mt-20">
              <h2 className="text-sm font-semibold text-ink">Description</h2>
              <p className="pd-text mt-3 max-w-2xl leading-relaxed">{data.description}</p>
            </section>
          )}

          {/* Used by — what references this RAG. */}
          <div id="sec-used-by" className="scroll-mt-20"><RagDependenciesPanel ragId={id} /></div>

          {/* Query history — the RAG's production query ledger. */}
          <div id="sec-queries" className="scroll-mt-20"><RagQueryHistoryPanel ragId={id} /></div>

          {/* Observability — health summary over this RAG's traces. */}
          {canReadExec && data.ragCode && (
            <div id="sec-observability" className="scroll-mt-20"><RagObservabilityPanel ragCode={data.ragCode} /></div>
          )}

          {/* Versions — bind this RAG to an artifact + recipe; validate and activate. */}
          <section id="sec-versions" className="ui-panel p-5 scroll-mt-20">
            <h2 className="text-sm font-semibold text-ink">Versions ({versions.length})</h2>
            {versions.length === 0 ? (
              <p className="mt-3 text-sm text-ink-muted">
                No versions yet. A version binds this RAG to one pipeline artifact and a retrieval recipe. Open the Designer to compose the first one.
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line">
                      <th className={th}>Version</th>
                      <th className={th}>Status</th>
                      <th className={th}>Artifact</th>
                      <th className={`${th} text-right`}>Eval</th>
                      <th className={th}>Created</th>
                      <th className={th}>Activated</th>
                      <th className={`${th} text-right`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>{versions.map((v) => (
                    <Fragment key={v._id}>
                      <tr className="ui-row border-b border-gray-50 last:border-0 dark:border-gray-700">
                        <td className={`${td} font-mono tabular-nums text-ink`}>v{v.version}</td>
                        <td className={td}><StatusChip tone={versionTone(v.status)}>{v.status}</StatusChip></td>
                        <td className={`${td} font-mono text-xs text-gray-600 dark:text-gray-300`}>{v.artifactCode}</td>
                        <td className={`${td} text-right tabular-nums ${v.evalScore != null && v.evalScore >= 0.7 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-700 dark:text-gray-200'}`}>{pct(v.evalScore)}</td>
                        <td className={`${td} tabular-nums text-gray-600 dark:text-gray-300`}>{formatDate(v.createdAt)}</td>
                        <td className={`${td} tabular-nums text-gray-600 dark:text-gray-300`}>{v.activatedAt ? formatDate(v.activatedAt) : '—'}</td>
                        <td className={`${td} whitespace-nowrap text-right`}>
                          <ActionButton kind="quiet" size="sm" label="Validate" onClick={() => validate(v.version)} />
                          {canActivate && (v.status === 'CANDIDATE' || v.status === 'EVAL_FAILED') && (
                            <ActionButton kind="quiet" size="sm" label="Activate" onClick={() => setConfirm({ kind: 'activate', version: v.version })} testId={`Rag.Activate.${v.version}`} />
                          )}
                        </td>
                      </tr>
                      {validation[v.version] && (
                        <tr><td colSpan={7} className="px-3 pb-3">
                          <Message severity={validation[v.version].ok ? 'success' : 'warn'} className="w-full"
                                   text={validation[v.version].ok ? `v${v.version} can serve queries.` : validation[v.version].problems.join(' · ')} />
                        </td></tr>
                      )}
                    </Fragment>
                  ))}</tbody>
                </table>
              </div>
            )}
            <p className="mt-3 border-t border-gray-100 pt-3 text-xs leading-relaxed text-gray-500 dark:border-gray-700 dark:text-gray-400">
              Activation needs an eval PASS on that exact version, newer than the version itself, when the policy requires it. A FAIL marks it EVAL_FAILED and leaves the active version untouched.
            </p>
          </section>
        </div>

        {/* Side rail: everything about the RAG that is not the recipe. */}
        <aside className="ui-panel p-5 lg:sticky lg:top-4">
          <h2 className="text-sm font-semibold text-ink">Details</h2>
          <dl className="pd-meta mt-4">
            <dt>Code</dt><dd className="font-mono text-xs">{data.ragCode}</dd>
            <dt>Status</dt><dd><StatusChip tone={data.status === 'ACTIVE' ? 'success' : 'neutral'}>{data.status}</StatusChip></dd>
            <dt>Serving</dt><dd>{data.activeVersion != null ? `v${data.activeVersion}` : 'Not serving'}</dd>
            <dt>Active artifact</dt><dd className="font-mono text-xs">{active?.artifactCode || '—'}</dd>
            <dt>Eval suite</dt>
            <dd>
              {data.defaultEvalSuiteRef
                ? <Link to={`/evals/${encodeId(data.defaultEvalSuiteRef)}`} className="text-blue-600 hover:underline">{suiteName}</Link>
                : suiteName}
            </dd>
            <dt>Last eval</dt><dd className="tabular-nums">{pct(data.lastEvalScore)}</dd>
            <dt>Project</dt>
            <dd>
              {data.appId
                ? <Link to={`/projects/${encodeId(data.appId)}`} className="text-blue-600 hover:underline">{projectLabel(data.appId)}</Link>
                : projectLabel(data.appId)}
            </dd>
          </dl>
          <hr className="pd-rule my-5" />
          <dl className="pd-meta">
            <dt>Created</dt><dd>{stamp(data.createdAt, createdByName)}</dd>
            <dt>Updated</dt><dd>{stamp(data.updatedAt, updatedByName)}</dd>
          </dl>

          {/* Jump nav — land on a section without scrolling the whole page. */}
          {navItems.length > 0 && (
            <>
              <hr className="pd-rule my-5" />
              <nav aria-label="RAG sections">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">RAG sections</h3>
                <ul className="flex flex-col gap-0.5">
                  {navItems.map((it) => {
                    const activeNav = activeSection === it.id;
                    return (
                      <li key={it.id}>
                        <button type="button" onClick={() => scrollToId(it.id)}
                          aria-current={activeNav ? 'true' : undefined}
                          className={`flex w-full items-center gap-1.5 border-l-2 py-1 pl-2 pr-2 text-left text-sm transition-colors ${
                            activeNav
                              ? 'border-blue-600 font-medium text-blue-600'
                              : 'border-transparent text-ink-muted hover:border-gray-300 hover:text-blue-600'
                          }`}>
                          <span className="text-xs opacity-60">›</span>
                          {it.label}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>
            </>
          )}
        </aside>
      </div>

      {/* Playground — ask this RAG a question, like the prompt's Test Run. */}
      <Dialog
        header={`Playground — ${data.name}`}
        visible={playgroundOpen}
        onHide={() => setPlaygroundOpen(false)}
        style={{ width: '60rem', maxWidth: '95vw' }}
        maximizable
        dismissableMask
        data-testid="Rag.Dialog.Playground"
      >
        <RagPlayground ragId={id} ragCode={data.ragCode} versions={versions} activeVersion={data.activeVersion} canQuery={canQuery} />
      </Dialog>

      <DeleteConfirmDialog visible={!!confirm} onCancel={() => setConfirm(null)}
        onDelete={() => (confirm?.kind === 'activate' && confirm.version != null ? activate.mutate(confirm.version)
          : confirm?.kind === 'deactivate' ? deactivate.mutate() : rollback.mutate())}
        message={confirm?.kind === 'activate'
          ? `Activate v${confirm.version}? The current active version is retired. If the eval policy is not met this is refused.`
          : confirm?.kind === 'deactivate'
            ? `Deactivate this RAG? v${data.activeVersion} is retired and every query to this RAG fails until a version is activated or rolled back. The RAG can then be deleted.`
            : 'Roll back to the previously active version? The current one is retired.'}
        testId="Rag.Dialog.Confirm" loading={activate.isPending || rollback.isPending || deactivate.isPending}
        header={confirm?.kind === 'activate' ? 'Confirm activation' : confirm?.kind === 'deactivate' ? 'Confirm deactivation' : 'Confirm rollback'}
        confirmLabel={confirm?.kind === 'activate' ? 'Activate' : confirm?.kind === 'deactivate' ? 'Deactivate' : 'Roll back'}
        loadingLabel={confirm?.kind === 'activate' ? 'Activating...' : confirm?.kind === 'deactivate' ? 'Deactivating...' : 'Rolling back...'}
        confirmSeverity={confirm?.kind === 'activate' ? 'success' : confirm?.kind === 'deactivate' ? 'danger' : 'warning'} />
    </div>
  );
};

export default RagView;
